import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useDropdownAnchor } from './useDropdownAnchor';
import { FLOW_PORTAL_PROPS } from '../useClickOutside';
import { usePeople } from '../usePeople';
import { filterPeopleByName, usePersonSearch } from './usePersonSearch';
import { MultiValueInput } from './MultiValueInput';
import { ME_TOKEN } from '@shared/flow/filter';
import type { FlowPeopleScope } from '@shared/flow/people';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

/**
 * Multi-select mode for `type: 'person'` filter conditions with
 * `operator: 'in_any_of'` (U9). Same scoped candidate list as
 * `PersonValuePicker` (`usePeople(scope)`, same search-filter helper —
 * `usePersonSearch`/`filterPeopleByName`, shared to avoid duplicating the
 * fetch-and-filter logic per "Reuse Before You Build") but checkbox-style
 * toggle selection with chips for the current selection, instead of
 * single-select.
 *
 * SAFETY PROPERTY (the actual reason this component exists as a distinct
 * mode rather than a flag on `PersonValuePicker`): `{me}` (`ME_TOKEN`) is
 * NEVER a selectable option here — no "You" section, no candidate row, no
 * chip. `selected` strips any `{me}` that somehow arrived in `value`
 * (e.g. a legacy non-array `in_any_of` state predating this component, or a
 * saved view written before this UI existed) defensively on read, so a
 * stale `{me}` can never render as a chip nor survive an unrelated edit.
 * This is the PRIMARY defense; the backend's `validateOperatorValueShape`
 * guard (unit 1, `src/shared/flow/filter-operators.ts`) is the write-time
 * BACKSTOP if this UI is ever bypassed (a saved view edited via direct API
 * call, for instance).
 *
 * On a people-endpoint failure, degrades to the generic `MultiValueInput`
 * free-entry chip control (already battle-tested for text/picklist
 * `in_any_of`) rather than an empty dead dropdown — plain text chips can't
 * contain `{me}` either, so the safety property holds through the fallback
 * too.
 */
export interface PersonMultiValuePickerProps {
  value: string[];
  scope?: FlowPeopleScope;
  onChange: (value: string[]) => void;
}

const CHIP_CLASS =
  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-bg-secondary dark:bg-nightshade-700 text-content-primary dark:text-nightshade-100 text-[11px]';

const CONTAINER_CLASS =
  'w-full flex flex-wrap items-center gap-1 px-1.5 py-1 min-h-[26px] text-[11.5px] border border-secondary dark:border-nightshade-700 rounded-md bg-white dark:bg-nightshade-800 cursor-pointer focus-within:ring-1 focus-within:ring-content-action';

export function PersonMultiValuePicker({ value, scope, onChange }: PersonMultiValuePickerProps) {
  const { open, setOpen, triggerRef, menuRef, menuStyle } = useDropdownAnchor();
  const peopleQuery = usePeople(scope);
  const [search, setSearch] = usePersonSearch(open);

  // Defensive strip, not just a UI omission: `{me}` must never render as a
  // chip nor be re-persisted by an unrelated edit (e.g. removing a DIFFERENT
  // chip), even if it arrived in `value` from outside this component.
  const selected = value.filter(v => v !== ME_TOKEN);

  if (peopleQuery.isError) {
    return <MultiValueInput value={selected} onChange={onChange} />;
  }

  const candidates = filterPeopleByName(peopleQuery.data?.people ?? [], search);

  const addPerson = (name: string) => {
    if (!selected.includes(name)) onChange([...selected, name]);
  };
  const removePerson = (name: string) => onChange(selected.filter(v => v !== name));

  return (
    <div className="relative" ref={triggerRef}>
      <div
        className={CONTAINER_CLASS}
        onClick={() => setOpen(o => !o)}
      >
        {selected.length === 0 && (
          <span className="text-content-placeholder">Choose…</span>
        )}
        {selected.map(name => (
          <span key={name} className={CHIP_CLASS}>
            {name}
            <button
              type="button"
              onClick={e => { e.stopPropagation(); removePerson(name); }}
              className="text-content-tertiary dark:text-nightshade-500 hover:text-content-danger dark:hover:text-red-400"
              title={`Remove ${name}`}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
      </div>
      {open && createPortal(
        <div
          ref={menuRef}
          data-testid="person-multi-picker-menu"
          {...FLOW_PORTAL_PROPS}
          style={menuStyle}
          className="w-[250px] rounded-lg border border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-800 shadow-lg overflow-hidden py-1"
        >
          <div className="px-2.5 pb-1">
            <input
              {...AUTOFILL_IGNORE_PROPS}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full px-2 py-1 text-[11.5px] border border-secondary dark:border-nightshade-700 rounded-md bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100 placeholder:text-content-placeholder focus:outline-none focus:ring-1 focus:ring-content-action"
            />
          </div>
          {peopleQuery.isLoading && (
            <div className="px-3 py-2 text-[12px] text-content-tertiary dark:text-nightshade-500">Loading…</div>
          )}
          {candidates.map(p => {
            const isSelected = selected.includes(p.name);
            return (
              <button
                key={p.sfUserId}
                type="button"
                onClick={() => (isSelected ? removePerson(p.name) : addPerson(p.name))}
                className={`flex items-center gap-1.5 w-full text-left px-2.5 py-1 text-[12px] ${
                  isSelected
                    ? 'bg-bg-blue-light text-content-action dark:text-blueberry-400 font-medium'
                    : 'text-content-primary dark:text-nightshade-100 hover:bg-bg-secondary dark:hover:bg-nightshade-700'
                }`}
              >
                <input type="checkbox" checked={isSelected} readOnly className="pointer-events-none" />
                {p.name}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
